import { Entity } from 'src/app/core/data/schema/entity';
import { Page } from './page';
import { Utilities } from 'src/app/core/helper/util';
import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { AppSettings } from 'src/app/core/configs/app.settings';

/**
 * Block type lets the dashboard editor know which component and 
 * associated configuration to use when editing a block
 */
export enum BlockType {
    Simple = 1,
    SideImage = 2,
    ItemSingle = 3,
    ItemMultiple = 4,
    Banner = 5,
    MemberPortal = 6,
    Column = 7,
    Posts = 8,
    Events = 9,
    Newsletter = 10,

    // entity media block, for rendering the media/title/body
    // in default layout as configured on media page. Used
    // for product, event, sponsor detail pages as well, with special
    // handling
    Media = 99,

    // entity detail blocks. These ones are added by the system on 
    // creation of entity pages, cannot be added manually and cannot be 
    // deleted, only disabled
    EventDetail = 100,
    ProductDetail = 101,
    SponsorDetail = 102,
    Category = 103,
    SurveyButton = 106, // button to take survey, cannot be disabled

    Footer = 104, // default footer with little config
    Sponsors = 105 // sponsor carousel with some option, to be implemented
}

export enum ButtonActionType {
    URL = 1,
    Anchor = 2, // needs ID on same page
    Item = 3, // would be some kind of relative url constructed from community url and item pk
}

export class PageBlock extends Entity {

    static className = 'PageBlock';
    static idField = 'pageBlockId';

    pageBlockId: number = 0;
    pageId: number = 0;
    type: BlockType = BlockType.Simple;

    bg: string;
    fg: string;
    paddingTb: string = '30px';
    margin: string = '0 0';
    align: string = 'center';

    // minHeight: string = '10vh';

    header: string;
    body: string;
    contentMaxWidth: string = '100%';

    imgPaths: string;
    videoUrl: string;

    buttonLbl: string;
    buttonFg?: string;
    buttonBg?: string;
    buttonAction?: string; // either an anchor (starts with #), url (starts wth http) or slug
    buttonAlign: string; // should be same as content alignment, but doesn't use text align

    sortOrder: number = 0;
    disabled: boolean = false;

    html: string;
    config: string;

    // this is used just in client, and will be whatever custom
    // config object a specific block needs. Never used anywhere
    // outside of block editing, deserialized and saved into config
    cfgObj: any;

    // populated for item blocks, either single or multiple, from the id's in itemPageIds
    // this is actually used to send subitems up and down to API
    subItems: Page[] = [];

    // this is used to sanitize HTML body content
    get sanitizedBody() {
        if (this.body != null) { return ServiceHelper.sanitizer.bypassSecurityTrustHtml(this.body) }
        else { return null }
    };

    // parsed array of image URLs
    imagesArray: string[] = [];

    constructor(pageBlock?: Partial<PageBlock>) {
        super();
        Object.assign(this, pageBlock);

        if (this.subItems != null) {
            this.subItems = this.subItems.map(m => new Page(m));
        }
    }

    // toJSON is called by JSON.stringify and gives an opportunity to change what is included
    // in the stringify output. In this case, we don't want to include cfgObj , 
    // since they are just used during edit, and not persisted
    toJSON() {
        const objWithoutId = {
            ...this,
            cfgObj: undefined,
        };
        return objWithoutId;
    }

    revert() {
        PageBlock.revert(PageBlock, this.pageBlockId);
    }


    get isSimple() { return this.type == BlockType.Simple; }
    get isSurveyButton() { return this.type == BlockType.SurveyButton; }
    get isSideImg() { return this.type == BlockType.SideImage; }
    get isBanner() { return this.type == BlockType.Banner; }
    get isItemSingle() { return this.type == BlockType.ItemSingle; }
    get isItemMultiple() { return this.type == BlockType.ItemMultiple; }
    get isMemberPortal() { return this.type == BlockType.MemberPortal; }
    get isPosts() { return this.type == BlockType.Posts; }
    get isEvents() { return this.type == BlockType.Events; }
    get isNewsletter() { return this.type == BlockType.Newsletter; }

    get isMedia() { return this.type == BlockType.Media; }
    get isCategory() { return this.type == BlockType.Category; }



    async uploadImgs(base64Imgs: string[]) {
        var paths = <string>await this.uploadBase64Images(base64Imgs, null, `${this.pageBlockId}/mediaimg`);
        if (Utilities.stringEmpty(this.imgPaths)) {
            this.imgPaths = paths;
        } else {
            this.imgPaths = this.imgPaths + '|' + paths
        }
    }

    // ----------------------------------------------------
    // Service-like features from here on down
    // ----------------------------------------------------

    /**
     * We will always be loading, displaying and manipulating blocks 
     * in batches, i.e. all blocks for a page, so caching just a single 
     * block like we do for pages doesn't make sense. Instead, we need
     * to cache the entire array, adding and removing from array as needed,
     * and comparing server copies of individual blocks to working copies
     * of individual blocks.
     * 
     */

    static cacheByParent: boolean = true;
    private static _cachedForParentId: number; // keep track of which page the blocks are for 
    private static _cachedServerObjs: PageBlock[]; // unmodified until saved
    private static _cachedObjs: PageBlock[]; // working copy
    protected static _cacheExpiry: number;

    // get all the child records for a specific parent, these will be cached for the last parent
    // that they were requested for
    public static async fetchForPage(pageId: number) {
        return await PageBlock.fetchChildrenFor(PageBlock, pageId, null, `forPage/${pageId}`);
    }

    // async saveForPage(busyState: PageState, pageId: number) {
    //     return await this.save(busyState, null, `forPage/${pageId}`);
    // }


    static async updateSortOrder(blocks: PageBlock[]) {
        await ServiceHelper.httpSvc.put(blocks, `${AppSettings.apiURL}/pageblocks/sortorder`, 'json');
    }
}
  